// src/lib/schema/base/website.js

import { shopInfoContainer } from '@/containers/shopInfo.container';
import { getLastModifiedDate, combineSchemasToGraph, cleanObject } from '@/lib/schema';

/**
 * Creates a comprehensive website schema
 * @param {string} lang - Language code
 * @param {object} pageData - Data for the current page
 * @returns {object} - JSON-LD schema for website
 */
export function generateWebsiteSchema(lang, pageData = {}) {
  const {
    baseURL,
    name: shopName,
    address,
    phone,
    socialMedia
  } = shopInfoContainer;

  // Extract page-specific data
  const title = pageData.title || shopName;
  const description = pageData.description || 'Premium carpets and rugs online';
  const lastModified = pageData.lastModified || getLastModifiedDate();

  // WebPage schema
  const webPageSchema = {
    '@type': 'WebPage',
    '@id': `${baseURL}/${lang}#webpage`,
    'url': `${baseURL}/${lang}`,
    'name': title,
    'description': description,
    'inLanguage': lang,
    'isPartOf': { '@id': `${baseURL}/#website` },
    'datePublished': pageData.datePublished || '2023-01-01T00:00:00+00:00',
    'dateModified': lastModified
  };

  // Add rating if available
  if (pageData.rating) {
    webPageSchema.aggregateRating = {
      '@type': 'AggregateRating',
      'ratingValue': pageData.rating,
      'reviewCount': pageData.reviewCount || 150,
      'bestRating': 5,
      'worstRating': 1
    };
  }

  // Website schema
  const websiteSchema = {
    '@type': 'WebSite',
    '@id': `${baseURL}/#website`,
    'url': baseURL,
    'name': shopName,
    'description': 'Premium carpets and rugs online',
    'publisher': { '@id': `${baseURL}/#organization` },
    'inLanguage': lang,
    // 'potentialAction': [  // TODO: Reactivate this when the searchbar is ready
    //   {
    //     '@type': 'SearchAction',
    //     'target': `${baseURL}/${lang}/shop/carpets?search={search_term_string}`,
    //     'query-input': 'required name=search_term_string'
    //   }
    // ]
  };

  // Organization schema
  const organizationSchema = {
    '@type': 'Organization',
    '@id': `${baseURL}/#organization`,
    'name': shopName,
    'url': baseURL,
    'logo': {
      '@type': 'ImageObject',
      'url': `${baseURL}/logo.png`,
      'width': 1440,
      'height': 1022
    }
  };

  // Add contact information
  if (phone) {
    organizationSchema.telephone = phone;
  }

  organizationSchema.email = 'info@yourcarpet.com';

  // Add social media links
  const socialLinks = [];
  if (socialMedia?.facebookPageURL) socialLinks.push(socialMedia.facebookPageURL);
  if (socialMedia?.instagramPageURL) socialLinks.push(socialMedia.instagramPageURL);
  if (socialMedia?.twitterPageURL) socialLinks.push(socialMedia.twitterPageURL);

  if (socialLinks.length > 0) {
    organizationSchema.sameAs = socialLinks;
  }

  // Add address if available
  if (address) {
    organizationSchema.address = {
      '@type': 'PostalAddress',
      'streetAddress': address.street || 'Av Luis Braille 18',
      'addressLocality': address.city || 'Marbella',
      'postalCode': address.postalCode || '29670',
      'addressCountry': address.country || 'Spain'
    };
  }

  // Clean objects to remove null/undefined values
  const cleanWebPageSchema = cleanObject(webPageSchema);
  const cleanWebsiteSchema = cleanObject(websiteSchema);
  const cleanOrganizationSchema = cleanObject(organizationSchema);

  // Combine all schemas into a graph
  return combineSchemasToGraph([cleanWebPageSchema, cleanWebsiteSchema, cleanOrganizationSchema]);
}
